import { LitElement, css, html } from 'lit';
import { SearchIcon, TopicCategory } from '../../types';
import { customElement, property, query, state } from 'lit/decorators';
import { delay, findIcon, getFromLocalStorage, isDefined } from '../../helpers/utils';

import { StateController } from '../../helpers/state/state-management/state-controller';
import topicData from '../../helpers/state/topic-data';

@customElement('category-selector')
export class CategorySelector extends LitElement {

    @query('#categoryName')
    categoryNameInput?: Element;

    @property({ type: String })
    previousValue?: string;

    @property({ type: Boolean })
    canAddCategory?: boolean = false;

    @property({ type: Object })
    editableCategory?: TopicCategory;

    @property({type: Boolean})
    dropdown: boolean = false;

    @state()
    dropdownOpen: boolean = false;

    @state()
    previewMode: boolean = false;

    amplitude_callsite = 'category-selector';

    value?: string;

    private readonly bindTopicState = new StateController(this, topicData);

    async connectedCallback() {
        super.connectedCallback();

        if ( this.value === undefined) {
            // allow a delay for the form to fully render. This doesn't really impact experience at the moment
            // because the form loads in the background
            await delay(1000);
            let autoSelectedCategory;

            // if there are lots of options and one isn't already set, we want to force a selection
            // dropdown is only on when there are more than 8
            // if the user has selected a category before, we want to keep that selection but keep this before the url check because the url check will override this
            const previous_stored =  getFromLocalStorage(`${topicData.topicId}_last_category`);
            const previous_stored_cat = topicData.topic?.categories?.find(c => c._id?.$oid === previous_stored);
            if (!this.dropdown && !this.previousValue) {
                const d = this.isWriter ? topicData.topic?.categories?.[0]?._id?.$oid : topicData.topic?.categories?.filter(c => c.can_crowdsource)?.[0]?._id?.$oid;
                autoSelectedCategory = previous_stored_cat ? previous_stored_cat._id?.$oid : d;
            } else if (!this.previousValue && previous_stored_cat) {
                autoSelectedCategory = previous_stored;
            }

            // check url parameters for categories and get the first in the comma separated list
            const urlParams = new URLSearchParams(window.location.search);
            const categoryNames = urlParams.get('categories');
            if (categoryNames !== null) {
                const categoryIds = categoryNames
                    .split(',')
                    .map(c => topicData.topic?.categories?.find(cat => cat.name.toLowerCase() === c.toLowerCase()))
                    .filter(isDefined)
                    .map(c => c._id?.$oid);
                if (categoryIds.length > 0) {
                    autoSelectedCategory = categoryIds[0];
                }
            }
            this.value = this.previousValue ?? autoSelectedCategory ?? undefined;
            this.dispatchEvent(
                new CustomEvent('select', {
                    detail: this.value,
                })
            );
            this.requestUpdate();
        };
    }

    get isWriter() {
        return this.previewMode ? false : topicData.isWriter;
    }

    private handleSelect(e: any) {
        this.value = e.detail;
        this.dispatchEvent(
            new CustomEvent('select', {
                detail: this.value,
            })
        );
        if (this.dropdown) {
            this.dropdownOpen = false;
        }
        this.requestUpdate();
    }

    reportValidity() {
        return this.value !== undefined;
    }

    private createNewCategory() {
        this.editableCategory = {
            _id: undefined,
            name: 'New Category',
            can_crowdsource: true,
            icon: 'https://img.icons8.com/?size=48&id=63763&format=png',
            icon_size: undefined,
        };
    }

    private cancelCreateNewCategory() {
        this.editableCategory = undefined;
    }

    private async saveNewCategory() {
        if (this.editableCategory !== undefined) {
            this.editableCategory.name = this.categoryNameInput?.textContent ?? 'New Category';
            this.editableCategory.icon = await this.getSuggestedIcon(this.editableCategory.name);
            const categories = [...topicData.topic?.categories ?? [], this.editableCategory];

            await topicData.fetchAndUpdateTopicData({categories}, this.amplitude_callsite, true, true);
            this.requestUpdate();
            this.editableCategory = undefined;
        }
    }

    private async getSuggestedIcon(category_name: string): Promise<string> {
        let suggestedIcons : SearchIcon[] = [];
        for (let i = 0; i < 3; i++) {
            suggestedIcons = await findIcon(
                this.amplitude_callsite,
                category_name,
                'stickers,emoji,poly,3d-fluency,officel'
            );
            if (suggestedIcons.length > 2) {
                break;
            } else if (i === 0 && category_name.endsWith('s')) {
                category_name = category_name.slice(0, -1);
            } else if (i === 1) {
                const words = category_name.split(' ');
                category_name = words[0];
            }
        }
        if (suggestedIcons.length === 0) {
            return 'https://img.icons8.com/?size=48&id=63763&format=png';
        }
        return `https://img.icons8.com/${suggestedIcons[0].platform}/${suggestedIcons[0].commonName}.png`;
    }

    protected render() {
        const availableCategories = topicData.topic?.categories?.filter(c => c.can_crowdsource);
        this.value = isDefined(this.previousValue) ? this.previousValue : this.dropdown ? undefined : availableCategories?.[0]?._id?.$oid;

        return html`
            <div class="pillsContainer ${!isDefined(this.previousValue) && !isDefined(this.value) ? 'invalid' : ''}">
                ${this.dropdown && !this.dropdownOpen ? html`
                    <div class="custom-dropdown">
                        <div class="pill-button" @click="${() => {this.dropdownOpen = !this.dropdownOpen;}}">
                            <category-pill .value=${availableCategories?.find(c => c._id?.$oid === this.value)}></category-pill>
                            <span class="arrow">${this.dropdownOpen ? '' : this.value ? 'Change' : 'Select'}</span>
                        </div>
                        ${this.dropdownOpen ? html`
                            <div class="dropdown-menu">
                                ${(this.isWriter ? topicData.topic?.categories : topicData.topic?.categories?.filter(c => c.can_crowdsource))?.map(
                                    (c) => html`
                                        <category-pill
                                            .value=${c}
                                            ?checked=${c._id?.$oid === this.value}
                                            @select=${this.handleSelect}>
                                        </category-pill>
                                    `
                                )}
                            </div>
                        ` : null}
                    </div>
                ` : html`
                    ${(this.isWriter ? topicData.topic?.categories : topicData.topic?.categories?.filter(c => c.can_crowdsource))?.map(
                        (c) => html`
                            <category-pill
                                .value=${c}
                                ?checked=${c._id?.$oid === this.value}
                                @select=${this.handleSelect}>
                            </category-pill>
                        `
                    )}
                    ${this.canAddCategory === true && this.isWriter && this.editableCategory === undefined
                        ? html`
                            <button @click="${this.createNewCategory}" class="tags-categories__item clickable">
                                <mwc-icon class="tags-categories__sell">add</mwc-icon>
                                <b>New Category</b>
                            </button>
                        `
                        : ''}
                    ${this.canAddCategory === true && this.isWriter && this.editableCategory !== undefined
                        ? html`
                            <div style="display: flex; align-items: center;">
                                <button @click="${this.createNewCategory}" class="tags-categories__item editable">
                                    <span id="categoryName" contenteditable="true">Type Name Here</span>
                                </button>
                                <mwc-icon class="action-icon" @click=${this.saveNewCategory}> check</mwc-icon>
                                <mwc-icon class="action-icon" @click=${this.cancelCreateNewCategory}>close</mwc-icon>
                            </div>
                        `
                        : ''}
                `}
            </div>
        `;
    }

    static get styles() {
        return css`
            :host {
                font-family: var(--custom-font), 'Montserrat', sans-serif;
            }

            .invalid {
                border: var(--main-warning-color) 2px solid;
                border-radius: 5px;
            }

            .arrow {
                font-size: 0.6rem;
                text-decoration: underline;
            }

            .pillsContainer {
                display: flex;
                flex-wrap: wrap;
                margin-top: 0.5rem;
            }

            .pill-button {
                display: flex;
                align-items: center;
                cursor: pointer;
            }

            category-pill {
                display: flex;
                align-items: center;
            }

            .clickable {
                cursor: pointer;
            }

            .editable {
                cursor: text;
            }

            .action-icon {
                --mdc-icon-size: 24px;
                margin-left: 8px;
                margin-bottom: 8px;
                cursor: pointer;
            }

            .tags-categories__item {
                background-color: white;
                border: 2px solid var(--theme-border-color);
                border-radius: 50px;
                padding: 2px 10px;
                margin-bottom: 4px;
                font-family: var(--custom-font) 'Montserrat';
            }

            .tags-categories__sell {
                --mdc-icon-size: 14px;
                vertical-align: text-bottom;
            }

            .tags-categories__item.over {
                outline: 2px dotted #666;
            }

            .tags-categories__item.tags-categories__item--group {
                background-color: #EFECF3;
            }

            .tags-categories__item.active {
                border: 3px solid var(--main-primary-color);
            }

            .tags-categories__item--image {
                vertical-align: text-bottom;
            }
        `;
    }
}
